import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`font-heading font-semibold text-lg md:text-xl whitespace-nowrap transition-colors ${
              scrolled || !isHome ? 'text-foreground-900' : 'text-white'
            }`}
          >
            Oopbuy Guide
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`whitespace-nowrap cursor-pointer px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                location.pathname === '/'
                  ? 'bg-primary-500 text-white'
                  : scrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                    : 'text-white/85 hover:text-white hover:bg-white/10'
              }`}
            >
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center inline mr-1.5"></i>
              Startseite
            </Link>

            <div className="relative group">
              <button
                className={`whitespace-nowrap cursor-pointer px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1 ${
                  scrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                    : 'text-white/85 hover:text-white hover:bg-white/10'
                }`}
              >
                <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center inline mr-1.5"></i>
                Shopping Guide
                <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-2 min-w-[220px]">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`whitespace-nowrap cursor-pointer px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                scrolled || !isHome
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-white/95 text-foreground-900 hover:bg-white'
              }`}
            >
              Buy It Now
            </a>
          </nav>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled || !isHome
                ? 'text-foreground-900 hover:bg-background-100'
                : 'text-white hover:bg-white/10'
            }`}
            aria-label="Menü öffnen"
          >
            <i className={`w-6 h-6 flex items-center justify-center ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-0 top-16 bg-background-50 z-40 transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full overflow-y-auto px-4 py-6">
          <nav className="space-y-1 mb-8">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-800 font-medium hover:bg-background-100 transition-colors"
            >
              <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
              Startseite
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-800 font-medium hover:bg-background-100 transition-colors"
            >
              <i className="ri-shopping-bag-3-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
              Buy It Now
            </a>
          </nav>

          <h3 className="px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
            Kategorien
          </h3>
          <nav className="space-y-1 mb-8">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
              >
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
                {cat.name}
              </a>
            ))}
          </nav>

          <h3 className="px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
            SEO Guides
          </h3>
          <nav className="space-y-1">
            <Link
              to="/artikel/oopbuy-spreadsheet-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
            >
              <i className="ri-article-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Kompletter Guide 2026
            </Link>
            <Link
              to="/artikel/sicherheitsguide"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
            >
              <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Sicherheitsguide
            </Link>
            <Link
              to="/artikel/versandguide"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
            >
              <i className="ri-ship-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Versandguide
            </Link>
            <Link
              to="/artikel/anfaenger-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
            >
              <i className="ri-user-star-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Anfänger Guide
            </Link>
            <Link
              to="/artikel/beste-sneaker"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
            >
              <i className="ri-footprint-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Beste Sneaker
            </Link>
            <Link
              to="/artikel/nike-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
            >
              <i className="ri-checkbox-blank-circle-fill w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Nike Guide
            </Link>
            <Link
              to="/artikel/jordan-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
            >
              <i className="ri-basketball-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Jordan Guide
            </Link>
            <Link
              to="/artikel/streetwear-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
            >
              <i className="ri-t-shirt-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Streetwear Guide
            </Link>
            <Link
              to="/artikel/verkaeufer-bewertung"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
            >
              <i className="ri-star-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Verkäufer Bewertung
            </Link>
            <Link
              to="/artikel/qc-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
            >
              <i className="ri-zoom-in-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              QC Guide
            </Link>
            <Link
              to="/artikel/preisvergleich"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
            >
              <i className="ri-money-euro-circle-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Preisvergleich
            </Link>
            <Link
              to="/artikel/alternativen"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 text-sm hover:bg-background-100 transition-colors"
            >
              <i className="ri-arrow-left-right-line w-4 h-4 flex items-center justify-center text-foreground-400"></i>
              Alternativen
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
}